import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import northLogo from "@/assets/north-logo.png"; // Fallback to local asset

/**
 * Hook to fetch the default logo URL from platform settings.
 * Falls back to local asset if not configured in database.
 * This allows hot-swapping logos (e.g., holiday themes) without code changes.
 */
export function useDefaultLogo() {
  const { data: logoUrl, isLoading } = useQuery({
    queryKey: ['platform-settings', 'default_logo_url'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('setting_value')
        .eq('setting_key', 'default_logo_url')
        .maybeSingle();

      if (error) {
        console.warn('Failed to fetch default logo URL from platform settings:', error);
        return northLogo; // Fallback to local asset
      }

      // Return the URL from database, or fallback to local asset
      return data?.setting_value || northLogo;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
    retry: 1,
    // Use local asset as placeholder data for instant rendering
    placeholderData: northLogo,
  });

  return {
    defaultLogoUrl: logoUrl || northLogo,
    isLoading,
  };
}
